/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { useState, useMemo, useContext, useCallback } from 'react';
import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import { useCurrentDisplayedMonthYearLogic } from './useCurrentDisplayedMonthYearLogic.js';
import { useFocusLogic } from './useFocusLogic.js';
import { usePopperTriggerLogic } from './usePopperTriggerLogic.js';
import { useKeyboardHandlers } from './useKeyboardHandlers.js';

export type CalendarContextT = ReturnType<typeof useCurrentDisplayedMonthYearLogic> &
  ReturnType<typeof useFocusLogic> &
  ReturnType<typeof usePopperTriggerLogic> &
  ReturnType<typeof useKeyboardHandlers> & {
    showCalendar: boolean;
    setShowCalendar: TypescriptHelpersT.StateSetter<boolean>;

    onPrevYearFocus: React.FocusEventHandler;
    onPrevMonthFocus: React.FocusEventHandler;
    onNextMonthFocus: React.FocusEventHandler;
    onNextYearFocus: React.FocusEventHandler;
  };

export const useConfigCalendarCTX = (): CalendarContextT => {
  const {
    trackFocusCalendarPrevYear,
    trackFocusCalendarNextYear,
    trackFocusCalendarPrevMonth,
    trackFocusCalendarNextMonth,
  } = useContext(ControlledDateTimePickerContext);
  const [showCalendar, setShowCalendar] = useState(false);

  const onPrevYearFocus = useCallback(() => {
    trackFocusCalendarPrevYear();
  }, [trackFocusCalendarPrevYear]);
  const onPrevMonthFocus = useCallback(() => {
    trackFocusCalendarPrevMonth();
  }, [trackFocusCalendarPrevMonth]);
  const onNextMonthFocus = useCallback(() => {
    trackFocusCalendarNextMonth();
  }, [trackFocusCalendarNextMonth]);
  const onNextYearFocus = useCallback(() => {
    trackFocusCalendarNextYear();
  }, [trackFocusCalendarNextYear]);

  const currentDisplayedMonthLogic = useCurrentDisplayedMonthYearLogic();

  const focusLogic = useFocusLogic({ currentDisplayedMonthLogic, showCalendar });
  const popperTriggerLogic = usePopperTriggerLogic({
    focusLogic,
    showCalendar,
    setShowCalendar,
    resetMonthYear: currentDisplayedMonthLogic.resetMonthYear,
  });
  const keyboardHandlers = useKeyboardHandlers({
    currentDisplayedMonthLogic,
    focusLogic,
  });

  return useMemo(
    () => ({
      showCalendar,
      setShowCalendar,

      onPrevYearFocus,
      onPrevMonthFocus,
      onNextMonthFocus,
      onNextYearFocus,

      ...currentDisplayedMonthLogic,
      ...focusLogic,
      ...popperTriggerLogic,
      ...keyboardHandlers,
    }),
    [
      showCalendar,
      onPrevYearFocus,
      onPrevMonthFocus,
      onNextMonthFocus,
      onNextYearFocus,
      currentDisplayedMonthLogic,
      focusLogic,
      popperTriggerLogic,
      keyboardHandlers,
    ],
  );
};
